import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../pages/components/Navbar";
import Footer from "../pages/components/Footer";
import { API_BASE_URL } from "../config";
import { useAppContext } from "../context/AppContext";
import {
  DynamicSimpleProductCard,
  DynamicDetailedProductCard,
  DynamicComboCard,
  normalizeImage,
  getItemKey,
} from "./components/ProductCard";
import FloatingWidgets from "./components/FloatingWidgets";
import SeoHelmet from "./components/SeoHelmet";
import CustomLoader from "./components/CustomLoader";

const locations = [
  "Peraiyur",
  "T.Kallupatti",
  "Simakkal",
  "Nagamalai Pudhukottai",
  "Kalavasal",
  "Krishnakovil",
  "Usilampatti",
  "Samayanallur",
  "Athipatti",
  "Kappalur Toll Plaza",
];

const fallbackStoreLocations = [
  {
    name: "Kalavasal",
    district: "Madurai",
    img: "assets/img/a1-img/map/A1_kalavasal_loc.jpg",
    link: "https://maps.app.goo.gl/w26rYpYzEqRjMqCz9",
    address:
      "Door No 73, Shop No G6 Service Rd, Bypass Rd, opp to Supreme Mobiles, Chokkalinga Nagar, Ponmeni, Madurai, Tamil Nadu 625016",
    phone: "7868066774",
    timing: "7:00AM - 10:00PM",
  },
  {
    name: "Peraiyur",
    district: "Madurai",
    img: "https://res.cloudinary.com/dsdxwhpuu/image/upload/v1788342632/peraiyur_ztsie8.jpg",
    link: "https://maps.app.goo.gl/SK5X8CrHsCfCnjt59",
    address:
      "Usilampatti Main Road, Near to the bus stand, Peraiyur, Tamil Nadu, 625703",
    phone: "8428446136",
    timing: "7:00AM - 10:00PM",
  },
  {
    name: "Samayanallur",
    district: "Madurai",
    img: "https://res.cloudinary.com/dsdxwhpuu/image/upload/v1788342631/samayanallur_l1xfti.jpg",
    link: "https://maps.app.goo.gl/BcaSpuKLwD83Ep4X7",
    address:
      "Chellam Complex, Madurai - Dindigul Rd, near by bus stop, Oormechikulam, Samayanallur, Madurai, Tamil Nadu 625402",
    phone: "9363071139",
    timing: "7:00AM - 10:00PM",
  },
  {
    name: "Simmakal",
    district: "Madurai",
    img: "https://res.cloudinary.com/dsdxwhpuu/image/upload/v1788342631/simakkal_thcdnw.jpg",
    link: "https://maps.app.goo.gl/zRp1iguVocZcr9WG7",
    address:
      "77, Bus Stand Road, Arajar Salai, Simmakkal, Madurai Main, Madurai, Tamil Nadu 625001",
    phone: "9626322024",
    timing: "7:00AM - 10:00PM",
  },
  {
    name: "Usilampatti",
    district: "Madurai",
    img: "assets/img/a1-img/map/A1_usilampatti_loc.jpg",
    link: "https://maps.app.goo.gl/AcE7sbQCCKnSrHU49",
    address:
      "Bus Depo, 99/100, Kochi-Madurai-Tondi Point Rd, opp. Royal Enfield Showroom, Sathya Nagar, Usilampatti, Tamil Nadu 625532",
    phone: "7550309803",
    timing: "7:00AM - 10:00PM",
  },
  {
    name: "T.Kallupatti",
    district: "Madurai",
    img: "https://res.cloudinary.com/dsdxwhpuu/image/upload/v1788342768/copy_of_tkallupatti_d9bzwa.jpg",
    link: "https://maps.app.goo.gl/xLMbDqJyGZrfDqgB9",
    address: "T.Kallupatti, Tamil Nadu 625702",
    phone: "8667351808",
    timing: "7:00AM - 10:00PM",
  },
  {
    name: "Athipatti",
    district: "Viruthunagar",
    img: "https://res.cloudinary.com/dsdxwhpuu/image/upload/v1788342632/athipatti_awfsba.jpg",
    link: "https://maps.app.goo.gl/JGEQQ2mWV2VwgUFu8",
    address: "Vandapuli Rd, Athipatti, Tamil Nadu 625705",
    phone: "9751815011",
    timing: "7:00AM - 10:00PM",
  },
  {
    name: "Kappalur Toll Plaza",
    district: "Madurai",
    img: "https://res.cloudinary.com/dsdxwhpuu/image/upload/v1788342632/kappalur_toll_dbpxpx.jpg",
    link: "",
    address: "Near Kappulur Toll Plaza",
    phone: "6381167583",
    timing: "7:00AM - 10:00PM",
  },
  {
    name: "Krishnakovil",
    district: "Viruthunagar",
    img: "assets/img/a1-img/map/A1_krishnankovil_loc.jpg",
    link: "https://maps.app.goo.gl/XkWm9yMQAVRu1JG79",
    address: "Krishnan Kovil, Tamil Nadu 626126",
    phone: "8940600701",
    timing: "7:00AM - 10:00PM",
  },
];

const videoItems = [
  {
    id: 1,
    src: "https://res.cloudinary.com/dsdxwhpuu/video/upload/v1787810809/intro_2_vbnw84.mp4",
    title: "Intro",
  },
  {
    id: 2,
    src: "https://res.cloudinary.com/dsdxwhpuu/video/upload/v1780480704/Mini_Jalebi_Small_in_Size_Big_in_Taste_Crispy_juicy_and_perfectly_coiled_bite-_vz1mkt.mp4",
    title: "Sweet Making",
  },
  {
    id: 3,
    src: "https://res.cloudinary.com/dsdxwhpuu/video/upload/v1780480706/Crunch_that_Speaks_Andhra_Tradition_Andhra_Murukku_the_perfect_blend_of_spice_crunch_n58907.mp4",
    title: "Snacks Preparation",
  },
  {
    id: 4,
    src: "https://res.cloudinary.com/dsdxwhpuu/video/upload/v1780480706/A1_Mutta_Puffs_Madurais_Most_Loved_Egg_Puff_Crispy_outside._Soft_spicy_inside._Ma_bgkhin.mp4",
    title: "Puff Preparation",
  }
];

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getCategoryParam = (category) => {
  if (!category) return "";
  if (typeof category === "object") {
    return category.slug || category._id || slugify(category.name || "");
  }
  return category;
};

const getProductCategoryName = (product, categoryMap = {}) => {
  if (!product?.category) return product?.categoryName || "Products";

  if (typeof product.category === "object") {
    return product.category.name || "Products";
  }

  const categoryId = String(product.category);
  return categoryMap[categoryId]?.name || product.categoryName || "Products";
};

const getProductCategorySlug = (product, categoryMap = {}) => {
  if (!product?.category) return "";

  if (typeof product.category === "object") {
    return (
      product.category.slug ||
      product.category._id ||
      slugify(product.category.name || "")
    );
  }

  const categoryId = String(product.category);
  return (
    categoryMap[categoryId]?.slug ||
    categoryMap[categoryId]?._id ||
    product.categorySlug ||
    ""
  );
};

const getEffectivePrice = (item) => {
  const price = Number(item?.price || 0);
  const offerPrice = Number(item?.offerPrice || 0);
  if (offerPrice > 0 && offerPrice < price) return offerPrice;
  return price;
};

const getOldPrice = (item) => {
  const price = Number(item?.price || 0);
  const offerPrice = Number(item?.offerPrice || 0);
  const mrp = Number(item?.mrp || 0);

  if (offerPrice > 0 && offerPrice < price) return mrp || price;
  if (mrp > price) return mrp;
  return 0;
};


const MOTHI_LADDU_VARIANTS = {
  "250g": { label: "250g", price: 149, mrp: 199 },
  "500g": { label: "500g", price: 289, mrp: 380 },
  "1kg": { label: "1 Kg", price: 549, mrp: 720 },
};

const formatPrice = (value) => `Rs. ${Math.round(Number(value || 0))}`;

const categoryMatches = (categoryName = "", keywords = []) => {
  const source = String(categoryName).toLowerCase();
  return keywords.some((keyword) => source.includes(keyword));
};

const getSellingPrice = (item) => Number(item?.price || 0);
const getStrikePrice = (item) => Number(item?.mrp || 0);

const parseWeightToGrams = (weight) => {
  const value = String(weight || "")
    .toLowerCase()
    .trim();

  if (value.endsWith("kg")) {
    return Number(value.replace("kg", "").trim()) * 1000;
  }

  if (value.endsWith("g")) {
    return Number(value.replace("g", "").trim());
  }

  return 0;
};

const formatWeight = (grams) => {
  if (grams >= 1000) {
    return `${grams / 1000}kg`;
  }
  return `${grams}g`;
};

const prepareProduct = (product, categoryMap) => {
  const categoryName = getProductCategoryName(product, categoryMap);
  const categorySlug = getProductCategorySlug(product, categoryMap);

  const basePrice = getSellingPrice(product);
  const baseMrp = getStrikePrice(product);
  const baseWeightGrams = parseWeightToGrams(product.weight || product.netwt);

  let availableWeightOptions = [];
  let isSoldOut = true;

  if (product.variants && product.variants.length > 0) {
    availableWeightOptions = product.variants
      .filter((variant) => variant.stock > 0)
      .map((variant) => ({
        weight: variant.weight,
        mrp: variant.mrp,
        price: variant.price,
        stock: variant.stock,
      }));

    if (availableWeightOptions.length > 0) {
      isSoldOut = false;
    }
  } else {
    if (baseWeightGrams > 0) {
      const optionsInGrams = [250, 500, 1000].filter(
        (g) => g <= baseWeightGrams,
      );

      availableWeightOptions = optionsInGrams.map((grams) => {
        const ratio = grams / baseWeightGrams;
        return {
          weight: formatWeight(grams),
          price: Math.round(basePrice * ratio),
          mrp: Math.round(baseMrp * ratio),
          stock: product.stock || 1,
        };
      });
    }
    if (product.stock > 0) {
      isSoldOut = false;
    }
  }

  const defaultOption =
    availableWeightOptions.find((o) => o.weight === "250g") ||
    availableWeightOptions[0] ||
    null;

  return {
    ...product,
    weightOptions: availableWeightOptions,
    img: normalizeImage(product.mainImage || product.image || product.img),
    categoryName,
    categorySlug,
    tag: product.tag || null,
    price: defaultOption ? defaultOption.price : basePrice,
    old_price: defaultOption ? defaultOption.mrp : baseMrp,
    quantity: product.quantity || 1,
    net_wt: defaultOption ? defaultOption.weight : product.weight || "Units",
    weights: availableWeightOptions.map((o) => o.weight),
    isSoldOut: isSoldOut,
  };
};

const prepareCombo = (combo) => {
  const basePrice = getEffectivePrice(combo);
  const baseMrp = getOldPrice(combo);
  const baseWeightGrams = 1000;

  const optionsInGrams = [250, 500, 1000];
  const weightOptions = optionsInGrams.map((grams) => {
    const ratio = grams / baseWeightGrams;
    return {
      weight: formatWeight(grams),
      price: Math.round(basePrice * ratio),
      mrp: Math.round(baseMrp * ratio),
      stock: combo.stock || 1,
    };
  });

  const availableWeightOptions = weightOptions.filter(
    (option) => option.stock > 0,
  );
  const isSoldOut = availableWeightOptions.length === 0;

  const defaultOption =
    availableWeightOptions.find((o) => o.weight === "250g") ||
    availableWeightOptions[0];

  return {
    ...combo,
    weightOptions,
    weights: weightOptions.map((o) => o.weight),
    img: normalizeImage(combo.mainImage || combo.image || combo.img),
    comboType: combo.comboType || combo.categoryName || combo.category?.name || "Combo",
    categoryName: combo.comboType || combo.categoryName || combo.category?.name || "Combo",
    price: defaultOption.price,
    old_price: defaultOption.mrp,
    quantity: combo.quantity || 1,
    net_wt: defaultOption.weight,
    slug: combo.slug || combo._id,
    isSoldOut: isSoldOut,
  };
};

const buildCategoryMap = (categories = []) =>
  categories.reduce((acc, category) => {
    acc[String(category._id)] = category;
    return acc;
  }, {});

const ProductSectionHeader = ({
  title,
  viewAllLink = "/categories",
  subtitle,
}) => (
  <div className="row align-items-end mb-10">
    <div className="col-xl-6 col-lg-6">
      <div className="bd-section__title-wrapper">
        <h2 className="bd-section__title">{title}</h2>
        {subtitle && <p className="mb-0 mt-10 text-muted">{subtitle}</p>}
      </div>
    </div>
    <div className="col-xl-6 col-lg-6 d-flex justify-content-lg-end">
      <div className="bd-section__arrow text-lg-end mb-10">
        <Link to={viewAllLink}>
          View All <i className="fa-regular fa-angle-right"></i>
        </Link>
      </div>
    </div>
  </div>
);

export default function Home() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const { addToCart, cartItems, user } = useAppContext();
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [sectionScroll, setSectionScroll] = useState(0);
  const bmHeroRef = useRef(null);
  const videoRefs = useRef([]);

  useEffect(() => {
    videoRefs.current.forEach((video) => {
      if (video) {
        video.muted = true;
        video.defaultMuted = true;
        video.volume = 0;
        video.setAttribute("muted", "");
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "true");

        const playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise.catch(() => { });
        }
      }
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (bmHeroRef.current) {
        const rect = bmHeroRef.current.getBoundingClientRect();
        const viewHeight = window.innerHeight;
        // Calculate scroll progress (0 when enters view, 1 when leaves)
        const progress = (viewHeight - rect.top) / (viewHeight + rect.height);
        setSectionScroll(Math.max(0, Math.min(1, progress)));
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [categories, setCategories] = useState([]);
  const [signatureProducts, setSignatureProducts] = useState([]);
  const [sweets, setSweets] = useState([]);
  const [combos, setCombos] = useState([]);
  const [savouries, setSavouries] = useState([]);
  const [bakeryItems, setBakeryItems] = useState([]);
  const [giftBoxes, setGiftBoxes] = useState([]);
  const [coolDrinks, setCoolDrinks] = useState([]);
  const [mothiLadduWeight, setMothiLadduWeight] = useState("250g");
  const [mothiLadduQuantity, setMothiLadduQuantity] = useState(1);

  const [storeLocations] = useState(fallbackStoreLocations);

  const categoryMap = useMemo(() => buildCategoryMap(categories), [categories]);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/content/banners`);
        const result = await res.json();

        // Use a more robust check for data structure (handles array or wrapped object)
        const bannerList = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : [];

        if (bannerList.length > 0) {
          const mappedSlides = bannerList.map((item, index) => ({
            id: item._id || index,
            subtitle: item.subtitle || item.subHeading || "",
            title: item.title || item.heading || item.name || "",
            description: item.description || "",
            image: normalizeImage(
              item.mainImage || item.image || item.bannerImage || "",
            ),
            primaryBtn: item.buttonText || null,
            secondaryBtn: item.secondaryBtn || null,
            primaryBtnLink: item.buttonLink || "#",
            secondaryBtnLink: item.secondaryBtnLink || "#",
            hasWheatIcon: item.hasWheatIcon || false,
          }));

          setSlides(mappedSlides);
        } else {
          setSlides([]);
        }
      } catch (error) {
        console.error("Error fetching banners:", error);
        setSlides([]);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    const fetchHomeDynamicData = async () => {
      setLoading(true);
      const minDelay = new Promise((resolve) => setTimeout(resolve, 4000));
      try {
        const [categoryRes, productRes, comboRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/content/categories?status=Published`),
          fetch(`${API_BASE_URL}/api/content/products?status=Published`),
          fetch(`${API_BASE_URL}/api/content/combos?status=Published`),
        ]);

        const [categoryJson, productJson, comboJson] = await Promise.all([
          categoryRes.json(),
          productRes.json(),
          comboRes.json(),
        ]);

        const categoryList = Array.isArray(categoryJson?.data)
          ? categoryJson.data
          : Array.isArray(categoryJson)
            ? categoryJson
            : [];

        const rawProducts = Array.isArray(productJson?.data)
          ? productJson.data
          : Array.isArray(productJson)
            ? productJson
            : [];

        const rawCombos = Array.isArray(comboJson?.data)
          ? comboJson.data
          : Array.isArray(comboJson)
            ? comboJson
            : [];

        const publishedCategories = categoryList.filter(
          (item) => !item.isDeleted && item.status === "Published",
        );

        const categoryLookup = buildCategoryMap(publishedCategories);

        const publishedProducts = rawProducts
          .filter((item) => !item.isDeleted && item.status === "Published")
          .map((item) => prepareProduct(item, categoryLookup));

        const publishedCombos = rawCombos
          .filter((item) => !item.isDeleted && item.status === "Published")
          .map(prepareCombo);

        const visibleCategories = [...publishedCategories].sort(
          (a, b) => Number(a.order || 0) - Number(b.order || 0),
        );

        const signatureList = publishedProducts.filter(
          (item) =>
            item.isSignature &&
            !item.isCombo &&
            !categoryMatches(item.categoryName, ["combo"]) &&
            !String(item.categorySlug || "").toLowerCase().includes("combo"),
        );

        const sweetsList = publishedProducts.filter((item) =>
          categoryMatches(item.categoryName, [
            "sweet",
            "laddu",
            "halwa",
            "mysore",
            "burfi",
            "barfi",
            "peda",
            "jamun",
            "rasgulla",
            "ghee",
            "soan",
            "jalebi",
          ]),
        );

        const savouriesList = publishedProducts.filter((item) =>
          categoryMatches(item.categoryName, [
            "snack",
            "savour",
            "savouri",
            "mixture",
            "namkeen",
            "crunch",
            "murukku",
            "chips",
          ]),
        );

        const bakeryList = publishedProducts.filter((item) =>
          categoryMatches(item.categoryName, [
            "bakery",
            "cake",
            "bread",
            "cookie",
            "biscuit",
          ]),
        );

        const giftBoxList = publishedProducts.filter((item) =>
          categoryMatches(item.categoryName, ["gift", "box", "hamper", "seer"]),
        );

        const coolDrinksList = publishedProducts.filter((item) =>
          categoryMatches(item.categoryName, [
            "drink",
            "beverage",
            "cool",
            "juice",
            "sarbath",
            "sharbat",
            "nannari",
            "milkshake",
            "rose milk",
          ]),
        );

        setCategories(visibleCategories);
        setSignatureProducts(signatureList);
        setSweets(sweetsList);
        setCombos(publishedCombos);
        setSavouries(savouriesList);
        setBakeryItems(bakeryList);
        setGiftBoxes(giftBoxList);
        setCoolDrinks(coolDrinksList);
      } catch (error) {
        console.error("Error fetching dynamic homepage data:", error);
        setCategories([]);
        setSignatureProducts([]);
        setSweets([]);
        setCombos([]);
        setSavouries([]);
        setBakeryItems([]);
        setGiftBoxes([]);
        setCoolDrinks([]);
      } finally {
        await minDelay;
        setLoading(false);
      }
    };

    fetchHomeDynamicData();
  }, []);

  useEffect(() => {
    if (!slides.length) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides]);

  const goToSlide = useCallback(
    (index) => {
      if (!slides.length || isTransitioning || index === currentSlide) return;
      setIsTransitioning(true);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 1200);
    },
    [slides.length, isTransitioning, currentSlide],
  );

  const nextSlide = useCallback(() => {
    if (!slides.length) return;
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, goToSlide, slides.length]);

  const prevSlide = useCallback(() => {
    if (!slides.length) return;
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, goToSlide, slides.length]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (window.innerWidth < 1024) return;
      const x = (e.clientX - window.innerWidth / 2) / 50;
      const y = (e.clientY - window.innerHeight / 2) / 50;
      setMousePos({ x, y });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    if (!slides.length) return;
    const timer = setInterval(nextSlide, 8000);
    return () => clearInterval(timer);
  }, [nextSlide, slides.length]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (isTransitioning || Math.abs(e.deltaY) < 30) return;
      if (e.deltaY > 0) nextSlide();
      else prevSlide();
    };

    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [isTransitioning, nextSlide, prevSlide]);

  useEffect(() => {
    if (loading) return; // Ensure carousels don't init while loader is active

    const swiperInstances = [];

    const initSwiper = (selector, options) => {
      const el = document.querySelector(selector);
      if (window.Swiper && el) {
        const instance = new window.Swiper(selector, options);
        swiperInstances.push(instance);
      }
    };

    setTimeout(() => {
      initSwiper(".bd-category-active", {
        slidesPerView: 7,
        spaceBetween: 20,
        centeredSlides: true,
        loop: true,
        autoplay: {
          delay: 2500,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
        speed: 600,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".category-button-next",
          prevEl: ".category-button-prev",
        },
        breakpoints: {
          1200: { slidesPerView: 7, spaceBetween: 20 },
          992: { slidesPerView: 5, spaceBetween: 18 },
          768: { slidesPerView: 3, spaceBetween: 16 },
          576: { slidesPerView: 3, spaceBetween: 14 },
          0: { slidesPerView: 1.6, spaceBetween: 12 },
        },
      });

      initSwiper(".bd-product-active", {
        slidesPerView: 4,
        spaceBetween: 30,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".product-button-next",
          prevEl: ".product-button-prev",
        },
        breakpoints: {
          1200: { slidesPerView: 5 },
          992: { slidesPerView: 3 },
          768: { slidesPerView: 4 },
          480: { slidesPerView: 2 },
          0: { slidesPerView: 1 },
        },
      });

      initSwiper(".bd-sweets-active", {
        slidesPerView: 4,
        spaceBetween: 30,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".sweets-button-next",
          prevEl: ".sweets-button-prev",
        },
        breakpoints: {
          1200: { slidesPerView: 4 },
          992: { slidesPerView: 3 },
          768: { slidesPerView: 2 },
          0: { slidesPerView: 1 },
        },
      });

      initSwiper(".bd-combo-active", {
        slidesPerView: 4,
        spaceBetween: 30,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".combo-button-next",
          prevEl: ".combo-button-prev",
        },
        breakpoints: {
          1200: { slidesPerView: 4 },
          992: { slidesPerView: 3 },
          768: { slidesPerView: 2 },
          0: { slidesPerView: 1 },
        },
      });

      initSwiper(".bd-savouries-active", {
        slidesPerView: 4,
        spaceBetween: 30,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".savouries-button-next",
          prevEl: ".savouries-button-prev",
        },
        breakpoints: {
          1200: { slidesPerView: 4 },
          992: { slidesPerView: 3 },
          768: { slidesPerView: 2 },
          0: { slidesPerView: 1 },
        },
      });

      initSwiper(".bd-bakery-active", {
        slidesPerView: 4,
        spaceBetween: 30,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".bakery-button-next",
          prevEl: ".bakery-button-prev",
        },
        breakpoints: {
          1200: { slidesPerView: 4 },
          992: { slidesPerView: 3 },
          768: { slidesPerView: 2 },
          0: { slidesPerView: 1 },
        },
      });

      initSwiper(".bd-gift-active", {
        slidesPerView: 4,
        spaceBetween: 30,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".gift-button-next",
          prevEl: ".gift-button-prev",
        },
        breakpoints: {
          1200: { slidesPerView: 5 },
          992: { slidesPerView: 3 },
          768: { slidesPerView: 2 },
          0: { slidesPerView: 1 },
        },
      });

      initSwiper(".bd-cooldrinks-active", {
        slidesPerView: 4,
        spaceBetween: 30,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".cooldrinks-button-next",
          prevEl: ".cooldrinks-button-prev",
        },
        breakpoints: {
          1200: { slidesPerView: 4 },
          992: { slidesPerView: 3 },
          768: { slidesPerView: 2 },
          0: { slidesPerView: 1 },
        },
      });

      initSwiper(".bd-video-active", {
        slidesPerView: 4,
        spaceBetween: 30,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".video-button-next",
          prevEl: ".video-button-prev",
        },
        on: {
          init: function () {
            this.el.querySelectorAll("video").forEach((v) => {
              v.muted = true;
              v.defaultMuted = true;
              v.volume = 0;
              v.setAttribute("muted", "");
            });
          },
        },
        breakpoints: {
          1200: { slidesPerView: 4 },
          992: { slidesPerView: 3 },
          768: { slidesPerView: 2 },
          0: { slidesPerView: 1 },
        },
      });

      initSwiper(".bd-map-active", {
        slidesPerView: 4,
        spaceBetween: 30,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".map-button-next",
          prevEl: ".map-button-prev",
        },
        breakpoints: {
          1200: { slidesPerView: 3 },
          992: { slidesPerView: 3 },
          768: { slidesPerView: 2 },
          0: { slidesPerView: 1 },
        },
      });
    }, 300);

    return () => {
      swiperInstances.forEach((swiper) => {
        if (swiper && typeof swiper.destroy === "function") {
          swiper.destroy(true, true);
        }
      });
    };
  }, [
    loading,
    categories.length,
    signatureProducts.length,
    combos.length,
    savouries.length,
    bakeryItems.length,
    giftBoxes.length,
    coolDrinks.length,
    storeLocations.length,
  ]);

  useEffect(() => {
    const total = cartItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const cartCountEl = document.querySelector(".cart-count");
    const cartTextEl = document.querySelector(".bd-cart__text h5");

    if (cartCountEl) cartCountEl.innerText = totalItems;
    if (cartTextEl) cartTextEl.innerText = `Rs. ${Math.round(total)}`;
  }, [cartItems]);

  const updateListItemByKey = (listSetter, itemKey, updater) => {
    listSetter((prevList) =>
      prevList.map((item) =>
        getItemKey(item) === itemKey ? updater(item) : item,
      ),
    );
  };

  const handleQuantityChange = (itemKey, listSetter, delta) => {
    updateListItemByKey(listSetter, itemKey, (item) => ({
      ...item,
      quantity: Math.max(1, Number(item.quantity || 1) + delta),
    }));
  };

  const handleProductWeightChange = (itemKey, listSetter, newWeight) => {
    updateListItemByKey(listSetter, itemKey, (item) => {
      const selected = item.weightOptions?.find((o) => o.weight === newWeight);

      return {
        ...item,
        net_wt: newWeight,
        price: selected?.price ?? item.price,
        old_price: selected?.mrp ?? item.old_price,
      };
    });
  };

  const showSuccessMessage = (selector) => {
    const element = document.querySelector(selector);
    if (element) {
      element.style.display = "flex";
      setTimeout(() => {
        element.style.display = "none";
      }, 2000);
    }
  };

  const handleAddToCart = (productToAdd, redirect = false) => {
    const newItem = {
      productId: productToAdd.slug || productToAdd._id,
      slug: productToAdd.slug,
      name: productToAdd.name,
      img: productToAdd.img,
      categoryName: productToAdd.categoryName,
      price: productToAdd.price,
      old_price: productToAdd.old_price,
      quantity: productToAdd.quantity || 1,
      net_wt: productToAdd.net_wt,
      weights: productToAdd.weights,
    };

    addToCart(newItem, redirect);

    if (redirect) {
      navigate("/checkout");
      return;
    }

    const cartOffcanvas = document.getElementById("cartmini");
    if (cartOffcanvas && window.bootstrap && window.bootstrap.Offcanvas) {
      const bsOffcanvas =
        window.bootstrap.Offcanvas.getOrCreateInstance(cartOffcanvas);
      bsOffcanvas.show();
    }
  };

  if (loading) {
    return <CustomLoader />;
  }

  return (
    <div>
      <SeoHelmet
        title="A1 Bakery & Sweets | Authentic Indian Delights in Madurai"
        description="Experience the finest ghee sweets, crunchy savouries, and fresh bakery items. Order online from A1 Bakery & Sweets, a trusted legacy of taste for over 35 years."
        keywords="A1 Bakery, Sweets Madurai, Indian Sweets Online, Ghee Sweets, Snacks Madurai"
      />
      <Navbar />

      <main>
        <section className="bd-hero-area p-relative">
          <div className="swiper-container bd-hero-active">
            <div ref={containerRef} className="hero-viewport">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`slide-container ${currentSlide === index ? "slide-active" : ""}`}
                >
                  <div
                    className="slide-bg"
                    style={{
                      backgroundImage: `url(${slide.image})`,
                      transform: "none",
                    }}
                  />

                  <div className="slide-content">
                    {slide.subtitle && (
                      <span className="slide-subtitle">{slide.subtitle}</span>
                    )}
                    <h1 className="slide-title">{slide.title}</h1>
                    {slide.description && (
                      <p className="slide-description">{slide.description}</p>
                    )}

                    {(slide.primaryBtn || slide.secondaryBtn) && (
                      <div className="button-group">
                        {slide.primaryBtn && (
                          <a href={slide.primaryBtnLink} className="btn-solid">
                            <span>{slide.primaryBtn}</span>
                          </a>
                        )}

                        {slide.secondaryBtn && (
                          <a
                            href={slide.secondaryBtnLink}
                            className="btn-outline"
                          >
                            {slide.secondaryBtn}
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  {slide.hasWheatIcon && (
                    <div
                      className="floating-wheat"
                      style={{
                        transform: `translate(${mousePos.x * 1.5}px, ${mousePos.y * 1.5}px) rotate(${mousePos.x * 0.5}deg)`,
                      }}
                    >
                      <svg viewBox="0 0 100 100">
                        <path d="M50 95c0-20 5-40 15-55M50 95c0-20-5-40-15-55M55 35c3-3 8-2 10 2s-1 10-5 12-8-8-5-14zM45 35c-3-3-8-2-10 2s1 10 5 12 8-8 5-14z" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}

              <div className="nav-container">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className="nav-item"
                    aria-label={`Go to slide ${index + 1}`}
                  >
                    <span
                      className={`nav-label ${currentSlide === index ? "label-active" : ""}`}
                    >
                      0{index + 1}
                    </span>
                    <div
                      className={`nav-line ${currentSlide === index ? "line-active" : ""}`}
                    />
                  </button>
                ))}
              </div>

              <div className="scroll-indicator">
                <div className="scroll-line" />
                <span className="scroll-text">Scroll</span>
              </div>
            </div>
          </div>

          <div className="bd-hero-bottom-belt d-none d-md-block">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <ul className="bd-hero-location-list">
                    {locations.map((location, index) => (
                      <li key={index}>{location}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bd-features__area pt-20 pb-15 d-none d-md-block">
          <div className="container">
            <div className="row justify-content-between">
              <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6">
                <div className="bd-features__item mb-20">
                  <div className="bd-features__content">
                    <h2>15+</h2>
                    <span>Branches</span>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6">
                <div className="bd-features__item mb-20">
                  <div className="bd-features__content">
                    <h2>150+</h2>
                    <span>Sweets</span>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6">
                <div className="bd-features__item mb-20">
                  <div className="bd-features__content">
                    <h2>60+</h2>
                    <span>Savouries</span>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6">
                <div className="bd-features__item mb-20">
                  <div className="bd-features__content">
                    <h2>400+</h2>
                    <span>Sweet Savouries</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 text-center">
                <p
                  className="mb-0 text-muted"
                  style={{ fontSize: "0.8rem", letterSpacing: "0.2px" }}
                >
                  * Variety counts represent our full range available in physical stores across branches. Online store offers a handpicked, curated selection.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bd-category__area mt-30 mb-10">
          <div className="container">
            <ProductSectionHeader
              title="Explore Our Products"
              subtitle="A curated collection of sweets, snacks, and celebration classics."
              viewAllLink="/categories"
            />

            <div className="row">
              <div className="col-12">
                <div className="p-relative">
                  <div className="bd-category-active swiper-container">
                    <div className="swiper-wrapper">
                      {categories.map((category, index) => (
                        <div
                          className="swiper-slide"
                          key={category._id || index}
                        >
                          <div className="bd-singel__category-2">
                            <div className="bd-category__img">
                              <Link
                                to={`/categories/${getCategoryParam(category)}`}
                              >
                                <img
                                  src={normalizeImage(category.mainImage)}
                                  alt={category.name}
                                />
                              </Link>
                            </div>
                            <div className="bd-category__text">
                              <h5>
                                <Link
                                  to={`/categories/${getCategoryParam(category)}`}
                                >
                                  {category.name}
                                </Link>
                              </h5>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bd-product__nav red-nav">
                    <button className="category-button-prev">
                      <i className="fa-regular fa-angle-left"></i>
                    </button>
                    <button className="category-button-next">
                      <i className="fa-regular fa-angle-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bd-product__area pb-30 signature-products-section">
          <div className="container">
            <ProductSectionHeader
              title="Signature Products"
              viewAllLink="/categories"
            />
            <div className="row">
              <div className="col-12">
                <div className="bd-product__wrapper p-relative">
                  <div className="bd-product-active swiper-container">
                    <div className="swiper-wrapper">
                      {signatureProducts.map((product, index) => (
                        <div
                          className="swiper-slide"
                          key={product._id || index}
                        >
                          <DynamicSimpleProductCard
                            product={product}
                            showTag={false}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bd-product__nav">
                    <button className="product-button-prev">
                      <i className="fa-regular fa-angle-left"></i>
                    </button>
                    <button className="product-button-next">
                      <i className="fa-regular fa-angle-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="position-relative">
          <div className="row align-items-center mb-40 mt-20">
            <Link to="/bulk-orders" className="col-12">
              <img
                src="https://res.cloudinary.com/dsdxwhpuu/image/upload/q_auto/f_auto/v1780115470/Bulk_orders_banner.jpg_op1ix0.jpg"
                alt="Bulk Orders"
                className="img-fluid rounded-3"
              />
            </Link>
          </div>
        </section>

        <section className="bd-product__area pb-60">
          <div className="container">
            <ProductSectionHeader
              title="Sweets"
              viewAllLink="/categories/sweets"
            />
            <div className="row">
              <div className="col-12">
                <div className="bd-product__wrapper p-relative">
                  <div className="bd-sweets-active swiper-container">
                    <div className="swiper-wrapper">
                      {sweets.map((product, index) => (
                        <div
                          className="swiper-slide"
                          key={product._id || index}
                        >
                          <DynamicDetailedProductCard
                            product={product}
                            originalIndex={index}
                            listSetter={setSweets}
                            onAddToCart={handleAddToCart}
                            onQuantityChange={handleQuantityChange}
                            onWeightChange={handleProductWeightChange}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bd-product__nav">
                    <button className="sweets-button-prev">
                      <i className="fa-regular fa-angle-left"></i>
                    </button>
                    <button className="sweets-button-next">
                      <i className="fa-regular fa-angle-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bd-product__area pb-60">
          <div className="container">
            <ProductSectionHeader title="Combos" viewAllLink="/combo_offer" />
            <div className="row">
              <div className="col-12">
                <div className="bd-product__wrapper p-relative">
                  <div className="bd-combo-active swiper-container">
                    <div className="swiper-wrapper">
                      {combos.map((product, index) => (
                        <div
                          className="swiper-slide"
                          key={product._id || index}
                        >
                          <DynamicComboCard
                            product={product}
                            originalIndex={index}
                            listSetter={setCombos}
                            onAddToCart={handleAddToCart}
                            onQuantityChange={handleQuantityChange}
                            onWeightChange={handleProductWeightChange}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bd-product__nav">
                    <button className="combo-button-prev">
                      <i className="fa-regular fa-angle-left"></i>
                    </button>
                    <button className="combo-button-next">
                      <i className="fa-regular fa-angle-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section ref={bmHeroRef} className="bm-hero mb-40">
          <div
            className="bm-hero__bg"
            style={{
              transform: `translateY(${(sectionScroll - 0.5) * 120}px) scale(${1.3 - sectionScroll * 0.1})`,
            }}
          ></div>
          <div className="bm-hero__overlay"></div>

          <div className="bm-hero__container">
            <div
              className="bm-hero__content-left"
              style={{
                transform: `translateY(${(sectionScroll - 0.5) * 80}px)`,
                opacity: sectionScroll > 0.05 ? 1 : 0,
                transition: "opacity 0.6s ease",
              }}
            >
              <p className="bm-hero__tagline">Crisp. Buttery. Addictive.</p>
              <p className="bm-hero__description">
                A Curated Collection Of Snacks, Sweets, And Celebration
                Classics.
              </p>
            </div>

            <div
              className="bm-hero__image-wrapper"
              style={{
                left: "50%",
                transform: `translate(calc(-50% - 40px), ${(sectionScroll - 0.5) * -180}px) rotate(${sectionScroll * 15}deg)`,
              }}
            >
              <img
                src="https://res.cloudinary.com/dsdxwhpuu/image/upload/v1788242283/murukku_gevtio.png"
                alt="Butter Murukku"
                className="bm-hero__image"
              />
            </div>

            <div
              className="bm-hero__content-right"
              style={{
                transform: `translateY(${(sectionScroll - 0.5) * -120}px)`,
              }}
            >
              <h1
                className="bm-hero__title"
                style={{
                  transform: `translateX(${(sectionScroll - 0.5) * -80}px) rotate(${(sectionScroll - 0.5) * -5}deg)`,
                  textShadow: "0 15px 30px rgba(0,0,0,0.3)",
                }}
              >
                Butter <br /> Murukku
              </h1>

              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="bm-hero__cta"
                style={{
                  transform: `translateY(${(sectionScroll - 0.5) * -40}px) scale(${0.95 + sectionScroll * 0.1})`,
                  boxShadow: `0 ${10 + sectionScroll * 20}px ${20 + sectionScroll * 30}px rgba(167, 34, 0, 0.4)`,
                }}
              >
                Grab Your Pack
              </a>
            </div>
          </div>
        </section>

        <section className="bd-product__area pb-60">
          <div className="container">
            <ProductSectionHeader
              title="Savouries & Crunchies"
              viewAllLink="/categories/savouries"
            />
            <div className="row">
              <div className="col-12">
                <div className="bd-product__wrapper p-relative">
                  <div className="bd-savouries-active swiper-container">
                    <div className="swiper-wrapper">
                      {savouries.map((product, index) => (
                        <div
                          className="swiper-slide"
                          key={product._id || index}
                        >
                          <DynamicDetailedProductCard
                            product={product}
                            originalIndex={index}
                            listSetter={setSavouries}
                            onAddToCart={handleAddToCart}
                            onQuantityChange={handleQuantityChange}
                            onWeightChange={handleProductWeightChange}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="product-psaviarieson banner-pagination-2"></div>
                  <div className="bd-product__nav">
                    <button className="savouries-button-prev">
                      <i className="fa-regular fa-angle-left"></i>
                    </button>
                    <button className="savouries-button-next">
                      <i className="fa-regular fa-angle-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="product-banner mb-40">
          <div className="banner-bg"></div>

          <img
            src="/assets/img/a1-img/dash__elements/dash_cashew.png"
            className="cashew cashew-1"
            alt=""
          />
          <img
            src="/assets/img/a1-img/dash__elements/dash_cashew1.png"
            className="cashew cashew-2"
            alt=""
          />
          <img
            src="/assets/img/a1-img/dash__elements/dash_cashew2.png"
            className="cashew cashew-3"
            alt=""
          />

          <div className="container">
            <div className="header_product_premium">
              <div className="left">
                <img
                  src="https://res.cloudinary.com/dsdxwhpuu/image/upload/v1788152226/modhi_laddu_vbeq1e.png"
                  className="laddu-img"
                  alt="Royal Mothi Laddu"
                />
              </div>

              <div className="right">
                <p className="subtitle">
                  Our <span>Signature</span> Masterpiece
                </p>

                <h1>Royal Mothi Laddu</h1>

                <p className="desc">
                  Delicate pearls of gram flour, fried in pure ghee and bound
                  with aromatic cardamom. A royal treat that melts in your
                  mouth.
                </p>

                <div className="weights">
                  {[
                    { key: "250g", label: "250g" },
                    { key: "500g", label: "500g" },
                    { key: "1kg", label: "1 Kg" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      className={mothiLadduWeight === opt.key ? "active" : ""}
                      onClick={() => setMothiLadduWeight(opt.key)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {(() => {
                  const currentItem =
                    MOTHI_LADDU_VARIANTS[mothiLadduWeight] ||
                    MOTHI_LADDU_VARIANTS["250g"];
                  return (
                    <>
                      <div className="price-row">
                        <span className="price">
                          Rs.<strong>{currentItem.price}</strong>
                        </span>
                        <span className="old-price">{currentItem.mrp}</span>

                        <div className="qty">
                          <button
                            type="button"
                            onClick={() =>
                              setMothiLadduQuantity(
                                Math.max(1, mothiLadduQuantity - 1),
                              )
                            }
                          >
                            -
                          </button>
                          <input
                            type="text"
                            value={mothiLadduQuantity}
                            readOnly
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setMothiLadduQuantity(mothiLadduQuantity + 1)
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="actions">
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleAddToCart({
                              name: "Royal Mothi Laddu",
                              price: currentItem.price,
                              old_price: currentItem.mrp,
                              img: "https://res.cloudinary.com/dsdxwhpuu/image/upload/v1787910906/modhi_laddu_vbeq1e.png",
                              quantity: mothiLadduQuantity,
                              net_wt: currentItem.label,
                            });
                          }}
                          className="bd-add__cart-btn-light"
                        >
                          <i className="far fa-shopping-bag"></i> Add To Cart
                        </a>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleAddToCart(
                              {
                                name: "Royal Mothi Laddu",
                                price: currentItem.price,
                                old_price: currentItem.mrp,
                                img: "https://res.cloudinary.com/dsdxwhpuu/image/upload/v1787910906/modhi_laddu_vbeq1e.png",
                                quantity: mothiLadduQuantity,
                                net_wt: currentItem.label,
                              },
                              true,
                            );
                          }}
                          className="bd-add__cart-btn"
                        >
                          <i className="far fa-shopping-bag"></i> Buy Now
                        </a>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* <section className="bd-product__area pb-60">
          <div className="container">
            <ProductSectionHeader
              title="Bakery Items"
              viewAllLink="/categories/bakery"
            />
            <div className="row">
              <div className="col-12">
                <div className="bd-product__wrapper p-relative">
                  <div className="bd-bakery-active swiper-container">
                    <div className="swiper-wrapper">
                      {bakeryItems.map((product, index) => (
                        <div
                          className="swiper-slide"
                          key={product._id || index}
                        >
                          <DynamicDetailedProductCard
                            product={product}
                            originalIndex={index}
                            listSetter={setBakeryItems}
                            onAddToCart={handleAddToCart}
                            onQuantityChange={handleQuantityChange}
                            onWeightChange={handleProductWeightChange}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bd-product__nav">
                    <button className="bakery-button-prev">
                      <i className="fa-regular fa-angle-left"></i>
                    </button>
                    <button className="bakery-button-next">
                      <i className="fa-regular fa-angle-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        {/* <section className="bd-product__area pb-60">
          <div className="container">
            <ProductSectionHeader
              title="Gift Boxes"
              viewAllLink="/categories/gift-boxes"
            />
            <div className="row">
              <div className="col-12">
                <div className="bd-product__wrapper p-relative">
                  <div className="bd-gift-active swiper-container">
                    <div className="swiper-wrapper">
                      {giftBoxes.map((product, index) => (
                        <div
                          className="swiper-slide"
                          key={product._id || index}
                        >
                          <DynamicSimpleProductCard product={product} />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bd-product__nav">
                    <button className="gift-button-prev">
                      <i className="fa-regular fa-angle-left"></i>
                    </button>
                    <button className="gift-button-next">
                      <i className="fa-regular fa-angle-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        <section className="video-area section-padding-80">
          <div className="container">
            <div className="bd-section__title-wrapper mb-30 text-center">
              <h2 className="bd-section__title">
                Explore A1 Bakery and Sweets
              </h2>
            </div>
            <div className="video-slider-wrap">
              <div className="swiper bd-video-active">
                <div className="swiper-wrapper">
                  {videoItems.map((item, index) => (
                    <div className="swiper-slide" key={item.id}>
                      <div className="video-card">
                        <video
                          ref={(el) => (videoRefs.current[index] = el)}
                          className="video-card-media"
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="auto"
                          onLoadedMetadata={(e) => {
                            e.currentTarget.muted = true;
                            e.currentTarget.defaultMuted = true;
                            e.currentTarget.volume = 0;
                          }}
                          onPlay={(e) => {
                            e.currentTarget.muted = true;
                          }}
                        >
                          <source src={item.src} type="video/mp4" />
                        </video>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bd-product__nav">
                <button className="video-button-prev">
                  <i className="fa-regular fa-angle-left"></i>
                </button>
                <button className="video-button-next">
                  <i className="fa-regular fa-angle-right"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="map__section mb-20">
          <div className="container">
            <ProductSectionHeader
              title="Visit Our Stores"
              subtitle="We are closer than you think. Walk in to experience the aroma of fresh baking."
              viewAllLink="/contact-us"
            />

            <div className="row">
              <div className="col-12">
                <div className="bd-product__wrapper p-relative">
                  <div className="bd-map-active swiper-container">
                    <div className="swiper-wrapper">
                      {storeLocations.map((item, index) => (
                        <div className="swiper-slide h-auto" key={index}>
                          <div className="card h-100 border-0 rounded-4 overflow-hidden d-flex flex-column">
                            <div
                              className="map__img-container position-relative overflow-hidden"
                              style={{ height: "230px" }}
                            >
                              <img
                                className="card-img-top object-fit-cover"
                                src={item.img}
                                alt={item.name}
                              />

                              {item.district && (
                                <span className="position-absolute top-0 start-50 translate-middle-x map-badge">
                                  {item.district}
                                </span>
                              )}
                            </div>

                            <div className="card-body p-4 d-flex flex-column">
                              <h4 className="card-title fw-bold text-uppercase text-dark">
                                {item.name}
                              </h4>

                              <hr />

                              <div className="flex-grow-1">
                                <div className="d-flex gap-2 mb-3">
                                  <p className="medium mb-0">{item.address}</p>
                                </div>
                              </div>

                              <div className="row g-2 mb-3">
                                <div className="col-6">
                                  <a
                                    href={`tel:${item.phone}`}
                                    className="h-100 p-2 bg-light border-0 rounded-3 d-flex align-items-center justify-content-center gap-2 text-decoration-none text-dark"
                                    style={{
                                      minHeight: "55px",
                                      backgroundColor: "#f8f9fa",
                                    }}
                                    title={`Call ${item.name}`}
                                  >
                                    <i className="fa-solid fa-phone text-danger"></i>
                                    <span className="fw-semibold small">
                                      {item.phone}
                                    </span>
                                  </a>
                                </div>

                                <div className="col-6">
                                  <div
                                    className="h-100 p-2 border-0 rounded-3 d-flex align-items-center justify-content-center gap-2"
                                    style={{
                                      minHeight: "55px",
                                      backgroundColor: "#f8f9fa",
                                    }}
                                  >
                                    <i className="fa-solid fa-clock text-danger"></i>
                                    <span className="fw-semibold small">
                                      {item.timing}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Button always at bottom */}
                              <div className="mt-3">
                                <a
                                  href={item.link}
                                  className="bm-hero__cta w-100 text-center m-0"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Get Directions
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bd-product__nav">
                    <button className="map-button-prev">
                      <i className="fa-regular fa-angle-left"></i>
                    </button>
                    <button className="map-button-next">
                      <i className="fa-regular fa-angle-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="social_media_area mb-60">
          <div class="container">
            <div class="row g-4 social_media">
              <div class="col-12 col-md-4">
                <div class="social-card position-relative">
                  <div class="card-accent accent-instagram"></div>

                  <div class="profile-container">
                    <div class="profile-ring ring-instagram">
                      <div class="profile-img">
                        <img
                          src="/assets/img/a1-img/Logo.png"
                          alt="Instagram Profile"
                          width={80}
                        />
                      </div>
                    </div>
                    <div class="verified-badge bg-instagram">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>

                  <div class="platform-name text-instagram">Instagram</div>
                  <div class="handle-name">@a1bakeryandsweets</div>

                  <div class="divider accent-instagram"></div>

                  <div class="count-text text-instagram">1k</div>
                  <div class="label-text">Followers</div>
                  <a
                    href="https://www.instagram.com/a1bakeryandsweets/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-social btn-instagram"
                  >
                    <i className="fab fa-instagram"></i>
                    Follow Us
                  </a>
                </div>
              </div>

              <div class="col-12 col-md-4">
                <div class="social-card position-relative">
                  <div class="card-accent accent-facebook"></div>

                  <div class="profile-container">
                    <div class="profile-ring ring-facebook">
                      <div class="profile-img">
                        <img
                          src="/assets/img/a1-img/Logo.png"
                          alt="Instagram Profile"
                          width={80}
                        />{" "}
                      </div>
                    </div>
                    <div class="verified-badge bg-facebook">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>

                  <div class="platform-name text-facebook">Facebook</div>
                  <div class="handle-name">a1bakeryandsweets</div>

                  <div class="divider accent-facebook"></div>

                  <div class="count-text text-facebook">1K</div>
                  <div class="label-text">Followers</div>

                  <a
                    href="https://www.facebook.com/a1sweeetsmadurai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-social btn-facebook"
                  >
                    <i className="fab fa-facebook-f"></i>
                    Like Page
                  </a>
                </div>
              </div>

              <div class="col-12 col-md-4">
                <div class="social-card position-relative">
                  <div class="card-accent accent-youtube"></div>

                  <div class="profile-container">
                    <div class="profile-ring ring-youtube">
                      <div class="profile-img">
                        <img
                          src="/assets/img/a1-img/Logo.png"
                          alt="Instagram Profile"
                          width={80}
                        />{" "}
                      </div>
                    </div>
                    <div class="verified-badge bg-youtube">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>

                  <div class="platform-name text-youtube">Youtube</div>
                  <div class="handle-name">@a1bakeryandsweets</div>

                  <div class="divider accent-youtube"></div>

                  <div class="count-text text-youtube">1K</div>
                  <div class="label-text">Subscribers</div>

                  <a
                    href="https://www.youtube.com/@a1sweetbakery500"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-social btn-youtube"
                  >
                    <i className="fab fa-youtube"></i>
                    Subscribe
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="feature-section">
          <div className="feature-container">
            <div className="feature-item">
              <img
                src="/assets/img/a1-img/dash__elements/fe-1.png"
                className="feature-icon"
                alt=""
              />
              <div className="feature-title">Business Model</div>
              <div className="feature-desc">
                Re-Investment Of Profit Back Into Business.
              </div>
            </div>

            <div className="feature-item">
              <img
                src="/assets/img/a1-img/dash__elements/fe-2.png"
                className="feature-icon"
                alt=""
              />
              <div className="feature-title">Double Employee</div>
              <div className="feature-desc">
                We Train And Keep Best The Skill & Soul Of Our 350 Employees
              </div>
            </div>

            <div className="feature-item">
              <img
                src="/assets/img/a1-img/dash__elements/fe-3.png"
                className="feature-icon"
                alt=""
              />
              <div className="feature-title">Nutritional Concious</div>
              <div className="feature-desc">
                To Boost Immunity People Started To Look For Nutritional Diets.
              </div>
            </div>

            <div className="feature-item">
              <img
                src="/assets/img/a1-img/dash__elements/fe-4.png"
                className="feature-icon"
                alt=""
              />
              <div className="feature-title">New Creations, Every Year</div>
              <div className="feature-desc">
                Each Year, We Introduce A New Product.
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWidgets />
    </div>
  );
}
